/**
 * The booth preview — one instance, owned by the /new shell and visible on
 * every result step (see Onboarding.tsx). Steps are controls; this is the
 * thing being controlled, so it never unmounts and never scrolls away.
 *
 * `variant` is layout only: 'rail' is the desktop side column, 'band' the
 * horizontal strip that sits above the step on narrow screens.
 */

import React from 'react'
import { FaMobileAlt, FaDesktop } from 'react-icons/fa'
import { useOnboardingStore } from '../onboardingStore'
import WelcomePreview from './WelcomePreview'

type PreviewVariant = 'rail' | 'band'

interface PreviewPaneProps {
  variant?: PreviewVariant
  displayWidth?: { portrait: number; landscape: number }
}

/** Wide enough to judge a design on; small enough to never crowd the controls. */
const VARIANT_WIDTH: Record<PreviewVariant, { portrait: number; landscape: number }> = {
  rail: { portrait: 216, landscape: 340 },
  band: { portrait: 84, landscape: 148 }
}

export const PreviewPane: React.FC<PreviewPaneProps> = ({ variant = 'rail', displayWidth }) => {
  const spec = useOnboardingStore((s) => s.spec)
  const welcomeDesign = useOnboardingStore((s) => s.welcomeDesign)
  const previewOrientation = useOnboardingStore((s) => s.previewOrientation)
  const setPreviewOrientation = useOnboardingStore((s) => s.setPreviewOrientation)
  const regenInFlight = useOnboardingStore((s) => s.regenInFlight)

  if (!spec || !welcomeDesign) return null

  // The Look step no longer draws its own preview, so a redraw has to read
  // here or the creator gets no feedback at all while it runs.
  const welcomeBusy =
    regenInFlight === 'welcome' ||
    regenInFlight === 'welcomeBgPortrait' ||
    regenInFlight === 'welcomeBgLandscape'

  const width = displayWidth ?? VARIANT_WIDTH[variant]
  const band = variant === 'band'

  const components =
    previewOrientation === 'portrait'
      ? welcomeDesign.orientedComponents.portrait
      : welcomeDesign.orientedComponents.landscape

  return (
    <div
      aria-busy={welcomeBusy}
      className={`${band ? 'flex items-center gap-3' : 'flex flex-col items-center gap-3'} ${
        welcomeBusy ? 'animate-pulse opacity-60' : ''
      }`}
    >
      {/* The band puts the device first and stacks its meta beside it; the
          rail reads top-down: toggle, device, palette. */}
      {band && (
        <WelcomePreview
          components={components}
          orientation={previewOrientation}
          backgroundColor={spec.palette.backgroundColor}
          displayWidth={width[previewOrientation]}
        />
      )}

      <div className={band ? 'flex flex-col items-start gap-2' : 'contents'}>
        <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.05] p-1">
          <button
            type="button"
            onClick={() => setPreviewOrientation('portrait')}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 text-[11px] font-medium transition-colors ${
              previewOrientation === 'portrait'
                ? 'bg-blueSparkle text-whiteout'
                : 'text-whiteout/50 hover:text-whiteout'
            }`}
          >
            <FaMobileAlt /> Phone
          </button>
          <button
            type="button"
            onClick={() => setPreviewOrientation('landscape')}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 text-[11px] font-medium transition-colors ${
              previewOrientation === 'landscape'
                ? 'bg-blueSparkle text-whiteout'
                : 'text-whiteout/50 hover:text-whiteout'
            }`}
          >
            <FaDesktop /> Laptop
          </button>
        </div>

        {!band && (
          <WelcomePreview
            components={components}
            orientation={previewOrientation}
            backgroundColor={spec.palette.backgroundColor}
            displayWidth={width[previewOrientation]}
          />
        )}

        <div className="flex items-center gap-2">
          {[
            spec.palette.backgroundColor,
            spec.palette.primaryColor,
            spec.palette.secondaryColor
          ].map((c, i) => (
            <span
              key={i}
              title={c}
              className="h-4 w-4 rounded-full ring-1 ring-white/20"
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default PreviewPane
